import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { DownOutlined } from '@ant-design/icons';
import { Button, Checkbox, Dropdown } from 'antd';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { intersection } from 'lodash';
import { useTranslation } from 'react-i18next';

import { DROPDOWN_TRIGGERS, FilterActions } from 'common-ui';
import { useAppDispatch, useAppSelector } from 'core/lib';
import { ContactBookType } from 'core/lib/constants/contactBook';
import { TypeOptions } from 'core/lib/constants/selects/contactBook';
import { selectContactBookTypeFilters, setContactBookFilters } from 'core/lib/modules/contactBook/store';

const ContactBookTypeFilter = () => {
  const [visible, setVisibility] = useState(false);
  const typeFilters = useAppSelector(selectContactBookTypeFilters);
  const [draftFilters, setDraftFilters] = useState(typeFilters);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const typeChecked = useCallback((type: ContactBookType) => draftFilters?.includes(type), [draftFilters]);

  useEffect(() => {
    setDraftFilters(typeFilters);
  }, [typeFilters]);

  const filtersUpdated = useMemo(() => {
    return (
      typeFilters?.length !== draftFilters?.length ||
      intersection(draftFilters, typeFilters).length !== typeFilters?.length
    );
  }, [draftFilters, typeFilters]);

  const onFilterUpdate = (type: ContactBookType) => {
    if (typeChecked(type)) {
      setDraftFilters(draftFilters?.filter((draftType) => draftType !== type));
    } else {
      setDraftFilters((draftFilters ?? []).concat(type));
    }
  };

  const onApply = () => {
    dispatch(setContactBookFilters({ type: draftFilters }));
  };

  const onClear = () => {
    setDraftFilters([]);
  };

  const items: ItemType[] = useMemo(() => {
    return [
      {
        key: 'type_group',
        type: 'group',
        className: 'max-h-64 overflow-y-auto p-0',
        children: TypeOptions?.map((item) => ({
          key: item.value,
          label: (
            <Checkbox
              onChange={() => onFilterUpdate(item.value)}
              checked={typeChecked(item.value)}
              className="flex flex-auto"
            >
              {item.label}
            </Checkbox>
          ),
        })),
      },
      { type: 'divider' },
      {
        type: 'group',
        key: 'status_actions',
        label: <FilterActions onClear={onClear} onApply={onApply} filtersUpdated={filtersUpdated} />,
      },
    ];
  }, [filtersUpdated, TypeOptions, typeChecked, t]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <Dropdown
        trigger={DROPDOWN_TRIGGERS}
        onOpenChange={(val) => setVisibility(val)}
        mouseLeaveDelay={0.2}
        open={visible}
        menu={{ items }}
      >
        <Button>
          {t('contactBook:filters.typeCount', { count: typeFilters?.length })} <DownOutlined />
        </Button>
      </Dropdown>
    </div>
  );
};

export default ContactBookTypeFilter;
